/**
 * InvestmentForm — the wealth-side form matching the legacy "Add a
 * holding" dialog exactly (see Figma screenshots 2–4).
 *
 * Writes to `wealth_holdings` with the SAME schema and preserves the
 * intelligence from Phase 64:
 *   • AMFI / Yahoo instrument search (`/api/instruments/search`) with
 *     debounced input + typed results (name + symbol + exchange + NAV).
 *   • Popularity nudge via `/api/instruments/<kind>/<symbol>/touch`.
 *   • Auto-update toggle written back as `auto_update`. The page-level
 *     `/api/wealth/refresh-prices` button then re-values holdings.
 *   • Deposit sub-fields (subtype / annual interest / principal /
 *     monthly contribution / compounding / start / maturity).
 *   • Graceful fallback if Phase 64 columns aren't yet migrated.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/CurrencyInput';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectSeparator } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, TrendingUp, Landmark, FileText, Sparkles, Wallet, Search as SearchIcon } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { CURRENCIES } from '@/lib/format';
import useCurrencyOptions from './useCurrencyOptions';

function currencySymbol(code) {
  return (CURRENCIES.find((c) => c.code === code) || {}).symbol || '';
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Pill selector options — matches the Figma design. */
const KIND_PILLS = [
  { value: 'mutual_fund', label: 'Mutual Funds', icon: TrendingUp },
  { value: 'stock',       label: 'Stocks',        icon: TrendingUp },
  { value: 'deposit',     label: 'Deposits',      icon: Landmark },
  { value: 'bond',        label: 'Bonds',         icon: FileText },
  { value: 'gold',        label: 'Gold',          icon: Sparkles },
  { value: 'other',       label: 'Other',         icon: Wallet },
];

/** Kinds that are eligible for live auto-pricing (AMFI/Yahoo). */
const AUTOPRICED = new Set(['mutual_fund', 'stock', 'etf']);

/** Columns added in Phase 64 — stripped and retried if PostgREST rejects. */
const PHASE64_KEYS = ['invested_value', 'quantity', 'symbol_or_isin', 'instrument_kind', 'auto_update', 'last_known_nav', 'last_priced_at'];

export default function InvestmentForm({ account = null, onCreate, onUpdate, onClose, baseCurrency = 'INR' }) {
  const { user } = useAuth();
  const editing = !!account;
  const currencyOpts = useCurrencyOptions();

  const [form, setForm] = useState({
    kind: 'mutual_fund',
    name: '',
    value: '',
    currency: baseCurrency,
    notes: '',
    // Cost-basis + auto-pricing (Phase 64)
    invested_value: '',
    quantity: '',
    symbol_or_isin: '',
    auto_update: true,
    // Deposit-specific
    deposit_subtype: 'fd',
    principal_amount: '',
    monthly_contribution: '',
    interest_rate: '',
    compounding_frequency: '4',
    start_date: '',
    maturity_date: '',
    // Phase 84.27 v3.6.0 — retirement + Shariah metadata. All optional.
    retirement_kind: '',       // '' | 'epf' | 'ppf' | 'nps' | 'ssy' | '401k' | 'roth_ira' | 'hsa' | 'super_apra' | 'eosb' | ...
    tax_treatment:   '',       // '' | 'pre_tax' | 'post_tax' | 'triple_advantage' | 'exempt' | 'taxable'
    shariah_compliant: false,
  });
  const [saving, setSaving] = useState(false);

  // Instrument search state (debounced). Keyed off `form.name` so the
  // Name field itself becomes the search input for MF/stock — no more
  // "Name + Find mutual fund" duplication (Phase 84.24 UX cleanup).
  const [instrumentResults, setInstrumentResults] = useState([]);
  const [instrumentLoading, setInstrumentLoading] = useState(false);
  const searchAbort = useRef(null);
  // Tracks the exact name string of the last picked result. While
  // `form.name` matches this, we do NOT re-fire the search (the user
  // has already made their choice).
  const pickedNameRef = useRef(null);

  useEffect(() => {
    if (editing && account?.raw) {
      const r = account.raw;
      setForm({
        kind: r.kind || 'mutual_fund',
        name: r.name || '',
        value: r.value != null ? String(r.value) : '',
        currency: r.currency || baseCurrency,
        notes: r.notes || '',
        invested_value:    r.invested_value    != null ? String(r.invested_value)    : '',
        quantity:          r.quantity          != null ? String(r.quantity)          : '',
        symbol_or_isin:    r.symbol_or_isin    || '',
        auto_update:       r.auto_update !== false,
        deposit_subtype:   r.deposit_subtype   || 'fd',
        principal_amount:  r.principal_amount  != null ? String(r.principal_amount)  : '',
        monthly_contribution: r.monthly_contribution != null ? String(r.monthly_contribution) : '',
        interest_rate:     r.interest_rate     != null ? String(r.interest_rate)     : '',
        compounding_frequency: r.compounding_frequency ? String(r.compounding_frequency) : '4',
        start_date:    r.start_date    || '',
        maturity_date: r.maturity_date || '',
        retirement_kind:   r.retirement_kind   || '',
        tax_treatment:     r.tax_treatment     || '',
        shariah_compliant: !!r.shariah_compliant,
      });
      // Editing an already-linked holding — treat the stored name as
      // "already picked" so we don't spam AMFI/Yahoo the moment the
      // dialog opens.
      if (r.symbol_or_isin && r.name) pickedNameRef.current = r.name;
    }
  }, [editing, account, baseCurrency]);

  // Debounced instrument search — kicks in after 250ms of quiet typing.
  // Runs against `form.name` directly so a single input drives both the
  // saved name AND the AMFI/Yahoo lookup.
  useEffect(() => {
    const q = (form.name || '').trim();
    if (!AUTOPRICED.has(form.kind) || !q || q.length < 2 || q === pickedNameRef.current) {
      setInstrumentResults([]); setInstrumentLoading(false); return;
    }
    if (searchAbort.current) searchAbort.current.abort();
    const ctrl = new AbortController();
    searchAbort.current = ctrl;
    setInstrumentLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const kindParam = form.kind === 'mutual_fund' ? 'mf' : form.kind;
        const r = await axios.get(`${BACKEND_URL}/api/instruments/search`, {
          params: { q, kind: kindParam, limit: 15 },
          headers: { Authorization: `Bearer ${session?.access_token || ''}` },
          signal: ctrl.signal,
          timeout: 45000,
        });
        setInstrumentResults(r.data?.rows || []);
      } catch (e) {
        if (!axios.isCancel(e)) console.warn('instrument search failed:', e?.message);
      } finally { setInstrumentLoading(false); }
    }, 250);
    return () => { ctrl.abort(); clearTimeout(t); };
  }, [form.name, form.kind]);

  const pickInstrument = useCallback(async (r) => {
    pickedNameRef.current = r.name;
    setForm((f) => ({
      ...f,
      name: r.name,
      symbol_or_isin: r.symbol,
      currency: f.currency || r.currency || baseCurrency,
    }));
    setInstrumentResults([]);
    // Popularity nudge — fire and forget.
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await axios.post(
        `${BACKEND_URL}/api/instruments/${r.kind}/${encodeURIComponent(r.symbol)}/touch`,
        null,
        { headers: { Authorization: `Bearer ${session?.access_token || ''}` }, timeout: 4000 },
      );
    } catch { /* silent */ }
  }, [baseCurrency]);

  // When the user hand-edits Name after picking, drop the symbol link so
  // we never mis-price a fund because the name no longer matches the
  // AMFI/Yahoo instrument.
  const handleNameChange = useCallback((e) => {
    const v = e.target.value;
    setForm((f) => {
      const next = { ...f, name: v };
      if (f.symbol_or_isin && v !== pickedNameRef.current) {
        next.symbol_or_isin = '';
      }
      return next;
    });
    if (v !== pickedNameRef.current) pickedNameRef.current = null;
  }, []);

  const buildRow = () => {
    const row = {
      kind: form.kind,
      name: form.name.trim(),
      value: Number(form.value || 0),
      currency: form.currency,
      notes: form.notes?.trim() || null,
    };
    if (form.kind === 'deposit') {
      row.deposit_subtype     = form.deposit_subtype;
      row.principal_amount    = form.deposit_subtype === 'fd' && form.principal_amount    ? Number(form.principal_amount)    : null;
      row.monthly_contribution = form.deposit_subtype === 'rd' && form.monthly_contribution ? Number(form.monthly_contribution) : null;
      row.interest_rate       = form.interest_rate  ? Number(form.interest_rate)  : null;
      row.compounding_frequency = form.compounding_frequency ? String(form.compounding_frequency) : null;
      row.start_date    = form.start_date    || null;
      row.maturity_date = form.maturity_date || null;
    } else {
      row.deposit_subtype = null;
      row.principal_amount = null;
      row.monthly_contribution = null;
      row.compounding_frequency = null;
      row.start_date = null;
      row.maturity_date = null;
      if (form.kind !== 'gold' && form.kind !== 'bond' && form.kind !== 'other') {
        row.interest_rate = null;
      }
    }
    // Phase 64 columns
    row.invested_value = form.invested_value && Number(form.invested_value) > 0 ? Number(form.invested_value) : null;
    row.quantity       = form.quantity       && Number(form.quantity)       > 0 ? Number(form.quantity)       : null;
    if (AUTOPRICED.has(form.kind)) {
      row.symbol_or_isin  = (form.symbol_or_isin || '').trim() || null;
      row.auto_update     = !!form.auto_update;
      row.instrument_kind = form.kind === 'mutual_fund' ? 'mf' : form.kind === 'stock' ? 'stock' : 'etf';
    } else {
      row.symbol_or_isin  = null;
      row.auto_update     = false;
      row.instrument_kind = null;
    }
    // Phase 84.27 v3.6.0 — retirement + Shariah metadata (all optional).
    row.retirement_kind   = form.retirement_kind ? form.retirement_kind : null;
    row.tax_treatment     = form.tax_treatment   ? form.tax_treatment   : null;
    row.shariah_compliant = !!form.shariah_compliant;
    return row;
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!form.name.trim()) { toast.error('Please give this a name.'); return; }
    if (!form.currency)    { toast.error('Please choose a currency.'); return; }
    if (form.kind === 'deposit') {
      if (!['fd', 'rd'].includes(form.deposit_subtype)) { toast.error('Pick FD or RD.'); return; }
      if (form.start_date && form.maturity_date && form.start_date >= form.maturity_date) {
        toast.error('Maturity date must be after start date.'); return;
      }
    }
    setSaving(true);
    try {
      const row = buildRow();
      const tryOnce = async (r) => (editing ? onUpdate('wealth_holdings', account.id, r) : onCreate('investments', r, user?.id));
      try {
        await tryOnce(row);
      } catch (err) {
        // Retry without Phase 64 columns if the schema hasn't migrated yet.
        const msg = `${err?.message || ''} ${err?.code || ''}`;
        if (/column .* does not exist|PGRST204/i.test(msg)) {
          const bare = { ...row };
          PHASE64_KEYS.forEach((k) => delete bare[k]);
          await tryOnce(bare);
          toast.warning('Saved without live-pricing fields (schema needs an update).');
        } else {
          throw err;
        }
      }
      toast.success(editing ? 'Saved.' : 'Added.');
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not save.');
    } finally { setSaving(false); }
  };

  const symbol = useMemo(() => currencySymbol(form.currency || baseCurrency), [form.currency, baseCurrency]);
  const showLookup    = AUTOPRICED.has(form.kind);
  const showDeposit   = form.kind === 'deposit';
  const showCostBasis = AUTOPRICED.has(form.kind); // MF / stock only surface cost basis + quantity

  return (
    <form onSubmit={save} className="space-y-4" data-testid="accounts-form-investment">
      {/* Type pill selector */}
      <div className="space-y-2">
        <Label>Type</Label>
        <div className="grid grid-cols-3 gap-2">
          {KIND_PILLS.map((k) => {
            const Icon = k.icon;
            const active = form.kind === k.value;
            return (
              <button
                key={k.value}
                type="button"
                onClick={() => setForm({ ...form, kind: k.value })}
                className={
                  'flex flex-col items-start gap-1.5 rounded-xl border px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ' +
                  (active
                    ? 'border-evergreen bg-sage-light/40 text-evergreen'
                    : 'border-border/70 hover:bg-muted/50 text-charcoal')
                }
                data-testid={`wealth-kind-pill-${k.value}`}
              >
                <Icon className={'h-4 w-4 ' + (active ? 'text-evergreen' : 'text-muted-foreground')} />
                <span className="text-sm font-medium">{k.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Name — for MF/stock this input doubles as the AMFI/Yahoo
          search. Typing shows a dropdown; picking sets the name AND
          the symbol. Hand-editing after picking clears the symbol so
          we never mis-price a fund. */}
      <div className="space-y-1.5 relative">
        <Label className="flex items-center gap-2">
          <span>Name</span>
          {showLookup && form.symbol_or_isin && (
            <Badge variant="outline" className="rounded-full text-[10px] font-mono" data-testid="wealth-symbol-badge">
              {form.symbol_or_isin}
            </Badge>
          )}
        </Label>
        <div className="relative">
          {showLookup && (
            <SearchIcon className="h-3.5 w-3.5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          )}
          <Input
            value={form.name}
            onChange={handleNameChange}
            placeholder={
              form.kind === 'mutual_fund' ? 'Start typing — S&P 500 Index Fund, HDFC Balanced…'
              : form.kind === 'stock'     ? 'Start typing — Apple, AAPL, Reliance…'
              : form.kind === 'deposit'   ? 'Fixed deposit / CD @ 7.2%'
              : form.kind === 'bond'      ? 'Bond / debenture…'
              : form.kind === 'gold'      ? 'Sovereign gold bond / coins…'
              : 'Investment…'
            }
            className={'rounded-xl ' + (showLookup ? 'pl-9' : '')}
            data-testid="wealth-name-input"
            autoFocus
            autoComplete="off"
          />
        </div>
        {showLookup && instrumentLoading && (
          <div className="text-[11px] text-muted-foreground px-2 flex items-center gap-1.5" data-testid="wealth-instrument-loading">
            <Loader2 className="h-3 w-3 animate-spin" />
            {form.kind === 'mutual_fund'
              ? 'Searching AMFI… first search may take ~30s while we load 14k schemes.'
              : 'Looking up ticker on Yahoo Finance…'}
          </div>
        )}
        {showLookup && !!instrumentResults.length && (
          <div className="max-h-56 overflow-y-auto rounded-xl border border-border/70 divide-y divide-border/40 bg-background shadow-sm" data-testid="wealth-instrument-results">
            {instrumentResults.map((r) => (
              <button
                type="button"
                key={`${r.kind}-${r.symbol}`}
                className="w-full text-left px-3 py-2 text-xs hover:bg-accent/30 transition-colors"
                data-testid={`wealth-instrument-pick-${r.symbol}`}
                onClick={() => pickInstrument(r)}
              >
                <div className="font-medium truncate">{r.name}</div>
                <div className="text-[10px] text-muted-foreground flex items-center gap-2 font-mono">
                  {r.symbol} · {r.exchange || r.kind} · {r.currency}
                  {r.last_nav != null && <span className="text-foreground/80">NAV {Number(r.last_nav).toFixed(2)}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
        {showLookup && (
          <p className="text-[11px] text-muted-foreground">
            {form.symbol_or_isin
              ? `Linked to ${form.kind === 'mutual_fund' ? 'AMFI' : 'Yahoo Finance'} · edit the name to unlink.`
              : `Pick a match to auto-price from ${form.kind === 'mutual_fund' ? 'AMFI NAV' : 'live market'}, or type any custom name and save.`}
          </p>
        )}
      </div>

      {/* Current value + currency */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Current value</Label>
          <CurrencyInput
            symbol={symbol}
            currency={form.currency}
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            placeholder="0"
            data-testid="wealth-value-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Currency</Label>
          <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
            <SelectTrigger className="rounded-xl" data-testid="wealth-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              {currencyOpts.top.map((c) => (
                <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code} — {c.label}</SelectItem>
              ))}
              <SelectSeparator />
              {currencyOpts.rest.map((c) => (
                <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code} — {c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-1.5">
        <Label>Notes (optional)</Label>
        <Input
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="rounded-xl"
          data-testid="wealth-notes-input"
        />
      </div>

      {/* Phase 84.27 v3.6.0 — Retirement / Shariah metadata.
          Collapsible details block; hidden by default so the form doesn't
          feel busy for users who don't need it. Country-specific label
          hints show up in the dropdown to help IN / US / AU / GCC users
          pick the right vehicle. */}
      <details className="rounded-xl border border-border/60 bg-muted/10 p-3" data-testid="wealth-retirement-details">
        <summary className="cursor-pointer text-sm font-medium select-none">
          Retirement / tax-advantaged? <span className="text-muted-foreground font-normal">(optional)</span>
        </summary>
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Vehicle</Label>
              <select
                value={form.retirement_kind}
                onChange={(e) => setForm({ ...form, retirement_kind: e.target.value })}
                className="w-full rounded-xl border border-input bg-transparent px-3 py-2 text-sm"
                data-testid="wealth-retirement-kind"
              >
                <option value="">— Not retirement —</option>
                <optgroup label="India">
                  <option value="epf">EPF (Employee Provident Fund)</option>
                  <option value="ppf">PPF (Public Provident Fund)</option>
                  <option value="nps">NPS (National Pension System)</option>
                  <option value="ssy">SSY (Sukanya Samriddhi)</option>
                  <option value="superannuation_in">Superannuation (IN)</option>
                </optgroup>
                <optgroup label="USA">
                  <option value="401k">401(k)</option>
                  <option value="403b">403(b)</option>
                  <option value="457">457</option>
                  <option value="traditional_ira">Traditional IRA</option>
                  <option value="roth_ira">Roth IRA</option>
                  <option value="hsa">HSA (Health Savings)</option>
                  <option value="sep_ira">SEP IRA</option>
                  <option value="solo_401k">Solo 401(k)</option>
                </optgroup>
                <optgroup label="Australia">
                  <option value="super_apra">Superannuation (APRA fund)</option>
                  <option value="super_smsf">SMSF</option>
                </optgroup>
                <optgroup label="Gulf">
                  <option value="eosb">EOSB (End of Service Benefit)</option>
                  <option value="gcc_pension">GCC national pension</option>
                </optgroup>
                <optgroup label="Other">
                  <option value="private_pension">Private pension</option>
                  <option value="annuity">Annuity</option>
                </optgroup>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tax treatment</Label>
              <select
                value={form.tax_treatment}
                onChange={(e) => setForm({ ...form, tax_treatment: e.target.value })}
                className="w-full rounded-xl border border-input bg-transparent px-3 py-2 text-sm"
                data-testid="wealth-tax-treatment"
              >
                <option value="">— Not set —</option>
                <option value="pre_tax">Pre-tax (deferred)</option>
                <option value="post_tax">Post-tax (Roth-style)</option>
                <option value="triple_advantage">Triple-advantage (HSA)</option>
                <option value="exempt">Exempt (EEE)</option>
                <option value="taxable">Taxable</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="wealth-shariah-label">
            <input
              type="checkbox"
              checked={form.shariah_compliant}
              onChange={(e) => setForm({ ...form, shariah_compliant: e.target.checked })}
              data-testid="wealth-shariah-compliant"
            />
            Shariah-compliant (Islamic finance)
          </label>
        </div>
      </details>

      {/* Cost basis + quantity + AMFI/Yahoo lookup */}
      {showCostBasis && (
        <div className="rounded-xl border border-border/60 bg-muted/20 p-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Invested value (cost basis)</Label>
              <CurrencyInput
                symbol={symbol}
                currency={form.currency}
                value={form.invested_value}
                onChange={(e) => setForm({ ...form, invested_value: e.target.value })}
                placeholder="0"
                data-testid="wealth-invested-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Quantity ({form.kind === 'mutual_fund' ? 'units' : 'shares'})</Label>
              <Input
                type="number" inputMode="decimal" step="0.0001"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                className="rounded-xl tabular-nums"
                placeholder="0"
                data-testid="wealth-quantity-input"
              />
            </div>
          </div>

          {showLookup && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={form.auto_update}
                onChange={(e) => setForm({ ...form, auto_update: e.target.checked })}
                data-testid="wealth-auto-update-toggle"
                disabled={!form.symbol_or_isin}
              />
              Auto-update value from {form.kind === 'mutual_fund' ? 'AMFI NAV' : 'live market price'} when I refresh
              {!form.symbol_or_isin && (
                <span className="text-[10px] italic ml-1">(pick a match above to enable)</span>
              )}
            </label>
          )}
        </div>
      )}

      {/* Deposit fields */}
      {showDeposit && (
        <div className="rounded-xl border border-border/60 bg-muted/20 p-3 space-y-3" data-testid="wealth-deposit-fields">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Deposit details</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Subtype</Label>
              <Select value={form.deposit_subtype} onValueChange={(v) => setForm({ ...form, deposit_subtype: v })}>
                <SelectTrigger className="rounded-xl" data-testid="deposit-subtype-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fd">FD — Fixed Deposit (lump sum)</SelectItem>
                  <SelectItem value="rd">RD — Recurring Deposit (monthly)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Annual interest %</Label>
              <Input
                type="number" inputMode="decimal" step="0.01"
                value={form.interest_rate}
                onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
                className="rounded-xl tabular-nums"
                placeholder="7.25"
                data-testid="deposit-interest-input"
              />
            </div>
            {form.deposit_subtype === 'fd' ? (
              <div className="space-y-1.5">
                <Label>Principal</Label>
                <CurrencyInput
                  symbol={symbol}
                  currency={form.currency}
                  value={form.principal_amount}
                  onChange={(e) => setForm({ ...form, principal_amount: e.target.value })}
                  data-testid="deposit-principal-input"
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Monthly contribution</Label>
                <CurrencyInput
                  symbol={symbol}
                  currency={form.currency}
                  value={form.monthly_contribution}
                  onChange={(e) => setForm({ ...form, monthly_contribution: e.target.value })}
                  data-testid="deposit-monthly-input"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Compounding / year</Label>
              <Select value={String(form.compounding_frequency)} onValueChange={(v) => setForm({ ...form, compounding_frequency: v })}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Annually</SelectItem>
                  <SelectItem value="2">Half-yearly</SelectItem>
                  <SelectItem value="4">Quarterly</SelectItem>
                  <SelectItem value="12">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Start date</Label>
              <Input type="date" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>Maturity date</Label>
              <Input type="date" value={form.maturity_date}
                onChange={(e) => setForm({ ...form, maturity_date: e.target.value })}
                className="rounded-xl" />
            </div>
          </div>
        </div>
      )}

      <DialogFooter className="sm:justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onClose} disabled={saving} className="rounded-xl">Cancel</Button>
        <Button type="submit" disabled={saving} className="rounded-xl" data-testid="wealth-save-button">
          {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
          {editing ? 'Save changes' : 'Save'}
        </Button>
      </DialogFooter>
    </form>
  );
}
